import {
  AddLevelsRequest,
  BareMatch,
  BareMyTeam,
  BareUser,
  ContractWithTeamInfo,
  Customer,
  DeviceType,
  GivenAward,
  MatchData,
  NoResponse,
  PagePlayerRank,
  PasswordReset,
  PlayerCard,
  RawUser,
  RegisterRequest,
  StreamContentPydanticModel,
  TypeaheadResult,
  UpdateAccountRequest,
  UpdateProfileRequest,
  User,
  UserRank,
  UserRecord,
  UsersApi,
} from "@virtualprogaming/js-public-sdk";
import { AxiosResponse } from "axios";
import { isFunction } from "lodash-es";
import { useMemo } from "react";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";

import { NOTIFICATIONS_SETTINGS } from "~/constants";
import { useApiConfig, useCurrentUser } from "~/utils";

import { useLogout } from "./auth";
import { useFcmUnsubscribe } from "./notifications";

export function useUserApi() {
  const config = useApiConfig();
  return useMemo(() => new UsersApi(...config), [config]);
}

export function useUserRegister(
  options?: UseMutationOptions<
    AxiosResponse<NoResponse>,
    unknown,
    RegisterRequest
  >
) {
  const api = useUserApi();
  return useMutation((rq: RegisterRequest) => api.register(rq), options);
}

export function useUserResetCodeSend(
  options?: UseMutationOptions<
    AxiosResponse<NoResponse>,
    unknown,
    { username: string }
  >
) {
  const api = useUserApi();
  return useMutation((rq) => api.sendResetCode(rq), options);
}

export function useUserPasswordReset(
  options?: UseMutationOptions<
    AxiosResponse<NoResponse>,
    unknown,
    PasswordReset
  >
) {
  const api = useUserApi();
  return useMutation((rq) => api.passwordReset(rq), options);
}

export function useUser(
  username?: string | undefined,
  options?: UseQueryOptions<User>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", username],
    queryFn: async () => {
      if (!username) {
        return {} as User;
      }
      const { data } = await api.getUser(username);
      return data;
    },
    ...options,
  });
}

export function useUserRecord(
  username: string,
  options?: UseQueryOptions<UserRecord>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", username, "record"],
    queryFn: async () => {
      const { data } = await api.getUserRecord(username);
      return data;
    },
    ...options,
  });
}

export function useUserRank(
  username: string,
  options?: UseQueryOptions<UserRank>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", username, "rank"],
    queryFn: async () => {
      const { data } = await api.getRank(username);
      return data;
    },
    ...options,
  });
}

export function useUserMedals(
  username: string,
  options?: UseQueryOptions<GivenAward[]>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", username, "medals"],
    queryFn: async () => {
      const { data } = await api.getMedals(username);
      return data;
    },
    ...options,
  });
}

export function useUserMyCustomer(options?: UseQueryOptions<Customer>) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", "customer"],
    queryFn: async () => {
      const { data } = await api.getMyCustomer();
      return data;
    },
    ...options,
  });
}

export function useUserMyAccount(options?: UseQueryOptions<RawUser>) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", "account"],
    queryFn: async () => {
      const { data } = await api.getMyAccount();
      return data;
    },
    ...options,
  });
}

export function useUserMyContracts(
  options?: UseQueryOptions<ContractWithTeamInfo[]>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["userContracts"],
    queryFn: async () => {
      const { data } = await api.getMyContracts();
      return data;
    },
    ...options,
  });
}
export function useUserMyTeams(options?: UseQueryOptions<BareMyTeam[]>) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["userTeamContracts"],
    queryFn: async () => {
      const { data } = await api.getMyTeams();
      return data;
    },
    ...options,
  });
}

export function useUserMyOutstandingReports(
  options?: UseQueryOptions<BareMatch[]>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", "outstandingReports"],
    queryFn: async () => {
      const { data } = await api.getMyOutstandingReports();
      return data;
    },
    ...options,
  });
}

export function useUserMyPlayerCards(options?: UseQueryOptions<PlayerCard[]>) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", "player_cards"],
    queryFn: async () => {
      const { data } = await api.getMyPlayerCards();
      return data;
    },
    ...options,
  });
}

export interface UpdatePlayerCardProps {
  teamId: number;
  playerCardId: number;
}

export function useUserPlayerCardUpdate(
  options?: UseMutationOptions<
    AxiosResponse<NoResponse>,
    unknown,
    UpdatePlayerCardProps
  >
) {
  const api = useUserApi();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options || {};
  return useMutation(
    (params) => api.updatePlayerCard(params.playerCardId, params.teamId),
    {
      onSuccess: async (data, vars, ctx) => {
        isFunction(onSuccess) && onSuccess(data, vars, ctx);
        await queryClient.invalidateQueries(["users", "account"]);
      },
      ...rest,
    }
  );
}

export function useUserPlayerCardRemove(
  options?: UseMutationOptions<AxiosResponse<NoResponse>>
) {
  const api = useUserApi();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options || {};
  return useMutation(() => api.removePlayerCard(), {
    onSuccess: async (data, vars, ctx) => {
      isFunction(onSuccess) && onSuccess(data, vars, ctx);
      await queryClient.invalidateQueries(["users", "account"]);
    },
    ...rest,
  });
}

export function useUserAccountUpdate(
  options?: UseMutationOptions<
    AxiosResponse<NoResponse>,
    unknown,
    UpdateAccountRequest
  >
) {
  const api = useUserApi();
  const queryClient = useQueryClient();
  const { data: user } = useUserMyAccount();
  const logout = useLogout();
  const { mutateAsync: fcmUnubscribe } = useFcmUnsubscribe();

  localStorage.removeItem("show_again_premium");
  const { onSuccess, ...rest } = options || {};
  return useMutation(
    (params: UpdateAccountRequest) => api.updateAccount(params),
    {
      onSuccess: async (data, vars, ctx) => {
        isFunction(onSuccess) && onSuccess(data, vars, ctx);
        await queryClient.invalidateQueries(["users", "account"]);
        if (user && vars.username !== user.username) {
          fcmUnubscribe(DeviceType.Web);
          localStorage.removeItem("show_again_premium");
          logout();
          localStorage.removeItem(NOTIFICATIONS_SETTINGS);
        }
      },
      ...rest,
    }
  );
}

export function useUserProfileUpdate(
  options?: UseMutationOptions<
    AxiosResponse<NoResponse>,
    unknown,
    UpdateProfileRequest
  >
) {
  const api = useUserApi();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options || {};
  return useMutation(
    (params: UpdateProfileRequest) => api.updateProfile(params),
    {
      onSuccess: async (data, vars, ctx) => {
        isFunction(onSuccess) && onSuccess(data, vars, ctx);
        await queryClient.invalidateQueries(["users", "profile"]);
      },
      ...rest,
    }
  );
}

export function useUserLevelAdd(
  options?: UseMutationOptions<
    AxiosResponse<NoResponse>,
    unknown,
    AddLevelsRequest
  >
) {
  const api = useUserApi();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options || {};
  return useMutation((params: AddLevelsRequest) => api.addLevels(params), {
    onSuccess: async (data, vars, ctx) => {
      isFunction(onSuccess) && onSuccess(data, vars, ctx);
      await queryClient.invalidateQueries(["users", currentUser?.username]);
    },
    ...rest,
  });
}

export function useUserImageUpload(
  options?: UseMutationOptions<AxiosResponse<NoResponse>, unknown, File>
) {
  const api = useUserApi();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options || {};
  return useMutation(
    (file) => {
      return api.uploadUserImage(file);
    },
    {
      onSuccess: async (data, vars, ctx) => {
        isFunction(onSuccess) && onSuccess(data, vars, ctx);
        await queryClient.invalidateQueries(["users", "account"]);
      },
      ...rest,
    }
  );
}

export function useUserRecentMatch(
  username: string,
  options?: UseQueryOptions<BareMatch>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", username, "recentMatch"],
    queryFn: async () => {
      const { data } = await api.getUserRecentMatch(username);
      return data;
    },
    ...options,
  });
}

export function useUserContracts(
  username: string,
  options?: UseQueryOptions<ContractWithTeamInfo[]>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["userContracts", username],
    queryFn: async () => {
      const { data } = await api.getUserContracts(username);
      return data;
    },
    ...options,
  });
}

export function useUserSeasons(
  username: string,
  options?: UseQueryOptions<number[]>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", username, "seasons"],
    queryFn: async () => {
      const { data } = await api.getUserSeasons(username);
      return data;
    },
    ...options,
  });
}

export function useUserMatchData(
  username: string,
  season: number,
  options?: UseQueryOptions<MatchData[]>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", username, "matchData", season],
    queryFn: async () => {
      const { data } = await api.getUserMatchData(username, season);
      return data;
    },
    ...options,
  });
}

export function useUserStreams(
  username: string,
  options?: UseQueryOptions<StreamContentPydanticModel[]>
) {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", username, "streams"],
    queryFn: async () => {
      const { data } = await api.getUserStreams(username);
      return data;
    },
    ...options,
  });
}

export function useUserForgotUsername(
  options?: UseMutationOptions<AxiosResponse<NoResponse>, unknown, string>
) {
  const api = useUserApi();
  const { onSuccess, ...rest } = options || {};
  return useMutation((mail: string) => api.forgotUsername(mail), {
    onSuccess: async (data, vars, ctx) => {
      isFunction(onSuccess) && onSuccess(data, vars, ctx);
    },
    ...rest,
  });
}

export function useUserResetPasswordByMail(
  options?: UseMutationOptions<AxiosResponse<NoResponse>, unknown, string>
) {
  const api = useUserApi();
  const { onSuccess, ...rest } = options || {};
  return useMutation((mail: string) => api.sendResetCodeEmail(mail), {
    onSuccess: async (data, vars, ctx) => {
      isFunction(onSuccess) && onSuccess(data, vars, ctx);
    },
    ...rest,
  });
}

export const useUserTypeahead = (
  search: string,
  options?: UseQueryOptions<TypeaheadResult[]>
) => {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", "search", "typeahead", search],
    queryFn: async () => (await api.typeahead(search)).data,
    ...options,
  });
};

export const useUserInfoById = (
  id: number | undefined,
  options?: UseQueryOptions<BareUser>
) => {
  const api = useUserApi();
  return useQuery({
    queryKey: ["user", id, "user-info"],
    queryFn: async () => {
      if (!id) return {} as BareUser;
      const { data } = await api.getUserinfosById(id);
      return data;
    },
    ...options,
  });
};

export const useUserInfoTypeahead = (
  id: number,
  options?: UseQueryOptions<TypeaheadResult>
) => {
  const api = useUserApi();
  return useQuery({
    queryKey: ["users", id, "username"],
    queryFn: async () => (await api.getUsername(id)).data,
    ...options,
  });
};

export const usePlayerRankings = (
  limit?: number,
  offset?: number,
  options?: UseQueryOptions<PagePlayerRank>
) => {
  const api = useUserApi();
  return useQuery({
    queryKey: ["player-rankings", limit, offset],
    queryFn: async () => {
      const { data } = await api.playersRanking(limit, offset);
      return data;
    },
    ...options,
  });
};
